import React, { useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { 
  Container, Typography, Box, Button, Table, TableBody, 
  TableCell, TableContainer, TableHead, TableRow, Paper, 
  IconButton, Chip, Dialog, DialogTitle, DialogContent, 
  DialogActions, TextField, FormControl, InputLabel, Select, 
  MenuItem, CircularProgress, Alert, Grid, Pagination
} from '@mui/material';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useAuthStore } from '../../store/authStore';

const BlogPosts = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthStore();
  
  // Определяем тип контента на основе URL
  const contentType = location.pathname.includes('/tips') ? 'tip' : 'blog';
  const isManagingTips = contentType === 'tip';
  
  // Настройки для разных типов контента
  const config = {
    title: isManagingTips ? 'Полезные советы' : 'Блог',
    createButtonText: isManagingTips ? 'Создать совет' : 'Создать статью',
    apiEndpoint: isManagingTips ? '/api/tips' : '/api/blog',
    editPath: isManagingTips ? '/admin/tips' : '/admin/blog',
    viewPath: isManagingTips ? '/tips' : '/blog'
  };
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [postToDelete, setPostToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  // Для администраторов показываем все посты по умолчанию
  const [statusFilter, setStatusFilter] = useState('all');

  // Check if user is admin
  useEffect(() => {
    if (user && !['admin', 'superadmin'].includes(user.role)) {
      navigate('/');
    }
  }, [user, navigate]);

  // Fetch posts (blog or tips)
  const fetchPosts = async () => {
    console.log(`=== НАЧАЛО ЗАГРУЗКИ ${isManagingTips ? 'СОВЕТОВ' : 'БЛОГ-ПОСТОВ'} ===`);
    console.log('Текущая страница:', page);
    console.log('Фильтр по статусу:', statusFilter);
    console.log('Тип контента:', contentType);
    console.log('Пользователь:', user ? user.name : 'не авторизован');
    
    try {
      setLoading(true);
      // Получаем токен из localStorage
      const token = localStorage.getItem('token');
      
      const response = await axios.get(config.apiEndpoint, {
        params: {
          page,
          limit: 10,
          status: statusFilter !== 'all' ? statusFilter : undefined
        },
        headers: {
          Authorization: token ? `Bearer ${token}` : ''
        }
      });
      
      console.log('Ответ от сервера:', response.data);
      
      // Обрабатываем разные форматы ответа
      const items = isManagingTips ? response.data.tips || response.data.blogPosts : response.data.blogPosts;
      console.log(`Полученные ${isManagingTips ? 'советы' : 'посты'}:`, items);
      console.log('Всего страниц:', response.data.totalPages);
      
      setPosts(items || []);
      setTotalPages(response.data.totalPages || 1);
      setLoading(false);
      console.log('Состояние после обновления:', { posts: response.data.blogPosts, totalPages: response.data.totalPages });
    } catch (err) {
      console.error(`=== ОШИБКА ПРИ ЗАГРУЗКЕ ${isManagingTips ? 'СОВЕТОВ' : 'БЛОГ-ПОСТОВ'} ===`);
      console.error(err);
      console.error('Детали ошибки:', err.response?.data || err.message);
      setError(`Не удалось загрузить ${isManagingTips ? 'советы' : 'статьи блога'}`);
      setLoading(false);
    } finally {
      console.log(`=== КОНЕЦ ЗАГРУЗКИ ${isManagingTips ? 'СОВЕТОВ' : 'БЛОГ-ПОСТОВ'} ===`);
    }
  };

  // Reset state when content type changes
  useEffect(() => {
    setPosts([]);
    setPage(1);
    setError(null);
    setStatusFilter('all');
  }, [location.pathname]);

  // Initial fetch
  useEffect(() => {
    fetchPosts();
  }, [page, statusFilter, location.pathname]);

  // Handle page change
  const handlePageChange = (event, value) => {
    setPage(value);
  };

  // Handle status filter change
  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(1);
  };

  // Open delete dialog
  const openDeleteDialog = (post) => {
    setPostToDelete(post);
    setDeleteDialogOpen(true);
  };

  // Close delete dialog
  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setPostToDelete(null);
    setDeleteError(null);
  };

  // Delete post
  const deletePost = async () => {
    if (!postToDelete) return;
    
    try {
      setDeleteLoading(true);
      await axios.delete(`${config.apiEndpoint}/${postToDelete.id}`);
      setDeleteLoading(false);
      closeDeleteDialog();
      fetchPosts();
    } catch (err) {
      console.error(`Error deleting ${isManagingTips ? 'tip' : 'blog post'}:`, err);
      setDeleteError(`Не удалось удалить ${isManagingTips ? 'совет' : 'статью'}`);
      setDeleteLoading(false);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return format(new Date(dateString), 'd MMM yyyy', { locale: ru });
  };

  // Get status chip color
  const getStatusColor = (status) => {
    switch (status) {
      case 'published':
        return 'success';
      case 'draft':
        return 'warning';
      default:
        return 'default';
    }
  };

  // Get status label
  const getStatusLabel = (status) => {
    switch (status) {
      case 'published':
        return 'Опубликовано';
      case 'draft':
        return 'Черновик';
      default:
        return status;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4 }, px: { xs: 1.5, sm: 3 } }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' }, 
        gap: { xs: 2, sm: 0 },
        mb: { xs: 2, sm: 4 } 
      }}>
        <Typography 
          variant="h4" 
          component="h1"
          sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}
        >
          Управление - {config.title}
        </Typography>
        <Button 
          component={Link} 
          to={`${config.editPath}/new`} 
          variant="contained" 
          color="primary" 
          startIcon={<AddIcon />}
        >
          {config.createButtonText}
        </Button>
      </Box>
      
      {/* Фильтры */}
      <Box sx={{ mb: { xs: 2, sm: 3 } }}>
        <FormControl variant="outlined" size="small" sx={{ minWidth: { xs: '100%', sm: 200 } }}>
          <InputLabel id="status-filter-label">Статус</InputLabel>
          <Select
            labelId="status-filter-label"
            id="status-filter"
            value={statusFilter}
            onChange={handleStatusFilterChange}
            label="Статус"
          >
            <MenuItem value="all">Все статьи</MenuItem>
            <MenuItem value="published">Опубликованные</MenuItem>
            <MenuItem value="draft">Черновики</MenuItem>
          </Select>
        </FormControl>
      </Box>
      
      {/* Таблица статей */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
      ) : posts.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1">
            {isManagingTips ? 'Советы не найдены' : 'Статьи не найдены'}
          </Typography>
        </Paper>
      ) : (
        <>
          <TableContainer component={Paper} sx={{ 
            overflowX: 'auto',
            '&::-webkit-scrollbar': {
              height: 8
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: theme.palette.primary.light,
              borderRadius: 4
            }
          }}>
            <Table size={isMobile ? 'small' : 'medium'}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ whiteSpace: 'nowrap', p: { xs: 1, sm: 2 } }}>Название</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', p: { xs: 1, sm: 2 } }}>Статус</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', p: { xs: 1, sm: 2 }, display: { xs: 'none', sm: 'table-cell' } }}>Дата публикации</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', p: { xs: 1, sm: 2 }, display: { xs: 'none', md: 'table-cell' } }}>Просмотры</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', p: { xs: 1, sm: 2 } }}>Действия</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {posts.map((post) => (
                  <TableRow key={post.id}>
                    <TableCell sx={{ p: { xs: 1, sm: 2 } }}>
                      <Typography variant="body2" noWrap sx={{ maxWidth: { xs: 150, sm: 200, md: 300 } }}>
                        {post.title}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ p: { xs: 1, sm: 2 } }}>
                      <Chip 
                        label={getStatusLabel(post.status)} 
                        color={getStatusColor(post.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={{ p: { xs: 1, sm: 2 }, display: { xs: 'none', sm: 'table-cell' } }}>
                      <Typography variant="body2" noWrap>
                        {formatDate(post.publishedAt)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ p: { xs: 1, sm: 2 }, display: { xs: 'none', md: 'table-cell' } }}>
                      {post.viewCount}
                    </TableCell>
                    <TableCell>
                      <IconButton 
                        component={Link} 
                        to={`${config.viewPath}/${post.slug}`} 
                        size="small" 
                        color="primary"
                        title="Просмотр"
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                      <IconButton 
                        component={Link} 
                        to={`${config.editPath}/edit/${post.id}`} 
                        size="small" 
                        color="primary"
                        title="Редактировать"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        color="error"
                        onClick={() => openDeleteDialog(post)}
                        title="Удалить"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Пагинация */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: { xs: 2, sm: 3 } }}>
              <Pagination 
                count={totalPages} 
                page={page} 
                onChange={handlePageChange} 
                color="primary" 
              />
            </Box>
          )}
        </>
      )}
      
      {/* Диалог подтверждения удаления */}
      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog}>
        <DialogTitle>Подтверждение удаления</DialogTitle>
        <DialogContent>
          {deleteError && (
            <Alert severity="error" sx={{ mb: 2 }}>{deleteError}</Alert>
          )}
          <Typography>
            Вы действительно хотите удалить {isManagingTips ? 'совет' : 'статью'} "{postToDelete?.title}"?
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            Это действие нельзя отменить.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} disabled={deleteLoading}>
            Отмена
          </Button>
          <Button 
            onClick={deletePost} 
            color="error" 
            variant="contained"
            disabled={deleteLoading}
          >
            {deleteLoading ? <CircularProgress size={24} /> : 'Удалить'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default BlogPosts;
